import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { App } from '@/services/app-compat';

export interface ToolbarItem {
  id: string;
  text: string;
  type?: string;
  overflow?: string;
  url?: string;
  href?: string;
  click?: ((e: React.MouseEvent) => void) | string;
  menuButtons?: ToolbarItem[];
}

export interface OrnaToolbarProps {
  sectionType: string;
  items: ToolbarItem[];
  activeTab?: string | null;
  onItemClick?: (item: ToolbarItem) => void;
  onMenuItemClick?: (item: ToolbarItem) => void;
}

export interface OrnaToolbarHandle {
  enableItem: (itemId: string, enable?: boolean) => void;
  blinkItem: (itemId: string, blink?: boolean) => void;
  updateItemText: (itemId: string, newText: string) => void;
  resize: () => void;
}

interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  item: ToolbarItem | null;
}

export const OrnaToolbar = forwardRef<OrnaToolbarHandle, OrnaToolbarProps>(
  function OrnaToolbar(props, ref) {
    const { sectionType, items, activeTab = null, onItemClick } = props;
    const { theme: currentTheme } = useTheme();

    const [menuOpen, setMenuOpen] = useState(false);
    const [messageCount, setMessageCount] = useState(0);
    const [disabledItems, setDisabledItems] = useState<Set<string>>(() => new Set());
    const [blinkingItems, setBlinkingItems] = useState<Set<string>>(() => new Set());
    const [visibleItemIds, setVisibleItemIds] = useState<string[]>([]);
    const [contextMenu, setContextMenu] = useState<ContextMenuState>({
      visible: false,
      x: 0,
      y: 0,
      item: null,
    });
    const [internalActiveTab, setInternalActiveTab] = useState<string | null>(activeTab);

    const rootRef = useRef<HTMLDivElement>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const isCalculatingOverflowRef = useRef(false);
    const menuTouchMoveHandlerRef = useRef<((e: TouchEvent) => void) | null>(null);
    const itemWidthCacheRef = useRef<Map<string, number>>(new Map());
    // Use a ref to always have the latest items for the resize observer callback
    const itemsRef = useRef(items);
    itemsRef.current = items;

    // -- Computed equivalents --

    const regularItems = useMemo(
      () => items.filter((item) => item.type !== 'splitButton'),
      [items]
    );

    const mainItems = useMemo(
      () =>
        regularItems.filter(
          (item) => visibleItemIds.includes(item.id) && item.overflow !== 'always'
        ),
      [regularItems, visibleItemIds]
    );

    const overflowItems = useMemo(() => {
      const alwaysOverflow = items.filter(
        (item) => item.overflow === 'always' && item.type !== 'splitButton'
      );
      const dynamicOverflow = regularItems.filter(
        (item) => !visibleItemIds.includes(item.id) && item.overflow !== 'always'
      );
      return [...dynamicOverflow, ...alwaysOverflow];
    }, [items, regularItems, visibleItemIds]);

    const splitButtonItem = useMemo(
      () => items.find((item) => item.type === 'splitButton'),
      [items]
    );

    const userMenuItems = useMemo(
      () => splitButtonItem?.menuButtons || [],
      [splitButtonItem]
    );

    const hasOverflowItems = overflowItems.length > 0;

    const effectiveActiveTab = internalActiveTab;

    // -- Methods --

    const calculateOverflow = useCallback(() => {
      if (isCalculatingOverflowRef.current) return;
      isCalculatingOverflowRef.current = true;

      const el = rootRef.current;
      if (!el) {
        isCalculatingOverflowRef.current = false;
        return;
      }

      const container = el.querySelector('.toolbar-container') as HTMLElement;
      const mainArea = el.querySelector('.toolbar-main') as HTMLElement;
      const menuBtn = el.querySelector('.toolbar-menu') as HTMLElement;

      if (!container || !mainArea || !menuBtn) {
        isCalculatingOverflowRef.current = false;
        return;
      }

      const currentRegularItems = itemsRef.current.filter(
        (item) => item.type !== 'splitButton'
      );
      const candidateItems = currentRegularItems.filter(
        (item) => item.overflow !== 'always'
      );

      if (candidateItems.length === 0) {
        setVisibleItemIds([]);
        isCalculatingOverflowRef.current = false;
        return;
      }

      const containerWidth = container.offsetWidth;
      const menuBtnWidth = menuBtn.offsetWidth;
      const padding = 8;
      const gap = 4;
      const availableWidth = containerWidth - menuBtnWidth - padding - gap;

      if (availableWidth <= 50) {
        setVisibleItemIds([]);
        isCalculatingOverflowRef.current = false;
        return;
      }

      // Measure visible buttons and cache their widths
      const buttons = Array.from(
        mainArea.querySelectorAll('.toolbar-btn')
      ) as HTMLElement[];
      for (const button of buttons) {
        const buttonId = button.getAttribute('id');
        if (!buttonId) continue;
        const w = button.offsetWidth;
        if (w > 0) {
          itemWidthCacheRef.current.set(buttonId, w);
        }
      }

      // Calculate which items fit using cached widths
      let totalWidth = 0;
      const newVisibleIds: string[] = [];

      for (let i = 0; i < candidateItems.length; i++) {
        const item = candidateItems[i];
        const w = itemWidthCacheRef.current.get(item.id) || 80; // fallback estimate
        const widthWithGap = w + (i > 0 ? gap : 0);

        if (totalWidth + widthWithGap <= availableWidth) {
          totalWidth += widthWithGap;
          newVisibleIds.push(item.id);
        } else {
          break;
        }
      }

      setVisibleItemIds(newVisibleIds);
      isCalculatingOverflowRef.current = false;
    }, []);

    const getItemUrl = useCallback(
      (item: ToolbarItem): string => {
        if (item.url) {
          return item.url;
        }

        if (item.href) {
          return item.href;
        }

        const section = sectionType;
        const menuId = item.id;
        const baseUrl = window.location.origin + window.location.pathname;

        const multiModuleItems = [
          'UserProfile', 'Map', 'Search', 'Messaging', 'Notes',
          'ActivityReport', 'CallCard', 'TourManagement', 'PaymentManagement',
          'GuardToursNew', 'CompanyDocuments', 'AnalyticsReport',
          'CallCardManagement', 'CitationManagement', 'ActivityReportManagement',
          'CaseReportManagement', 'EmergencyLookup', 'InProgressActiveCardsFullScreen',
          'InProgressPanic', 'CaseReports', 'CallAlertManagement',
          'CustomLocationManagement', 'VehicleManagement', 'CallSignManagement',
          'CompanySettings', 'OrnaSupportAttachments', 'CustomFormsManagement',
          'AddressReview',
        ];

        const multiURLSegment =
          multiModuleItems.indexOf(menuId) !== -1 ? '&multiModule=true' : '';

        return `${baseUrl}?sectionName=${section}&tab=${menuId}&function=${section}_${menuId}${multiURLSegment}`;
      },
      [sectionType]
    );

    const shouldShowContextMenu = useCallback((item: ToolbarItem): boolean => {
      if (item.url || item.href) {
        return true;
      }

      const excludedItems = [
        'btnGettingStarted',
        'btnKnowledgeCenter',
        'btnReturnHome',
        'btnLogOut',
        'PanicButton',
        'InProgressPanic',
        'SelfInitiate',
        'StatusUpdate',
      ];

      if (excludedItems.indexOf(item.id) !== -1) {
        return false;
      }

      return true;
    }, []);

    const handleItemClick = useCallback(
      (item: ToolbarItem, e: React.MouseEvent) => {
        e.stopPropagation();

        if (disabledItems.has(item.id)) {
          return;
        }

        setInternalActiveTab(item.id);

        if (typeof item.click === 'function') {
          item.click(e);
        } else if (typeof item.click === 'string') {
          const globalFunc = (window as any)[item.click];
          if (typeof globalFunc === 'function') {
            globalFunc(e.nativeEvent);
          }
        }

        setMenuOpen(false);

        if (onItemClick) {
          onItemClick(item);
        }
      },
      [disabledItems, onItemClick]
    );

    const toggleMenu = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      setMenuOpen((prev) => !prev);
      setContextMenu((prev) => ({ ...prev, visible: false }));
    }, []);

    const handleContextMenu = useCallback(
      (item: ToolbarItem, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!shouldShowContextMenu(item)) {
          return;
        }

        setMenuOpen(false);

        setContextMenu({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          item: item,
        });
      },
      [shouldShowContextMenu]
    );

    const openInNewTab = useCallback(
      (item: ToolbarItem) => {
        const url = getItemUrl(item);
        window.open(url, '_blank');
        setContextMenu((prev) => ({ ...prev, visible: false }));
      },
      [getItemUrl]
    );

    const openInNewWindow = useCallback(
      (item: ToolbarItem) => {
        const url = getItemUrl(item);
        window.open(url, '_blank', 'width=1200,height=800');
        setContextMenu((prev) => ({ ...prev, visible: false }));
      },
      [getItemUrl]
    );

    const copyLink = useCallback(
      (item: ToolbarItem) => {
        const url = getItemUrl(item);
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url);
        } else {
          const input = document.createElement('input');
          input.value = url;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          document.body.removeChild(input);
        }
        setContextMenu((prev) => ({ ...prev, visible: false }));
      },
      [getItemUrl]
    );

    // -- Imperative handle for external callers --

    const enableItem = useCallback((itemId: string, enable: boolean = true) => {
      setDisabledItems((prev) => {
        const next = new Set(prev);
        if (enable) {
          next.delete(itemId);
        } else {
          next.add(itemId);
        }
        return next;
      });
    }, []);

    const blinkItem = useCallback((itemId: string, blink: boolean = true) => {
      setBlinkingItems((prev) => {
        const next = new Set(prev);
        if (blink) {
          next.add(itemId);
        } else {
          next.delete(itemId);
        }
        return next;
      });
    }, []);

    const updateItemText = useCallback(
      (itemId: string, newText: string) => {
        // Since items are props and we cannot mutate them directly in React,
        // we mutate the item object in place to match the Vue behavior,
        // which expects external callers to have a reference to the same items array.
        const item = items.find((i) => i.id === itemId);
        if (item) {
          item.text = newText;
          // Force a re-render by toggling a counter
          setDisabledItems((prev) => new Set(prev));
        }
      },
      [items]
    );

    const resize = useCallback(() => {
      calculateOverflow();
    }, [calculateOverflow]);

    useImperativeHandle(
      ref,
      () => ({
        enableItem,
        blinkItem,
        updateItemText,
        resize,
      }),
      [enableItem, blinkItem, updateItemText, resize]
    );

    const toggleMenuImperative = useCallback(() => {
      setMenuOpen((prev) => !prev);
    }, []);

    // -- Expose imperative handle on container DOM element for legacy code --
    useEffect(() => {
      const el = rootRef.current;
      const container = el?.parentElement;
      const handle = { enableItem, blinkItem, updateItemText, resize, toggleMenu: toggleMenuImperative };
      if (el) {
        (el as any).__ornaToolbarRef = handle;
      }
      if (container) {
        (container as any).__ornaToolbarRef = handle;
      }
      return () => {
        if (el) {
          delete (el as any).__ornaToolbarRef;
        }
        if (container) {
          delete (container as any).__ornaToolbarRef;
        }
      };
    }, [enableItem, blinkItem, updateItemText, resize, toggleMenuImperative]);

    // -- Sync activeTab prop to internal state --

    useEffect(() => {
      setInternalActiveTab(activeTab ?? null);
    }, [activeTab]);

    // -- Touch scroll prevention when menu is open --

    useEffect(() => {
      if (menuOpen) {
        const handler = (e: TouchEvent) => {
          const el = rootRef.current;
          if (!el) return;
          const menu = el.querySelector('.combined-menu');
          if (menu && menu.contains(e.target as Node)) {
            return;
          }
          e.preventDefault();
        };
        menuTouchMoveHandlerRef.current = handler;
        document.addEventListener('touchmove', handler, { passive: false });
      } else {
        if (menuTouchMoveHandlerRef.current) {
          document.removeEventListener('touchmove', menuTouchMoveHandlerRef.current);
          menuTouchMoveHandlerRef.current = null;
        }
      }

      return () => {
        if (menuTouchMoveHandlerRef.current) {
          document.removeEventListener('touchmove', menuTouchMoveHandlerRef.current);
          menuTouchMoveHandlerRef.current = null;
        }
      };
    }, [menuOpen]);

    // -- Mount / unmount effects --

    useEffect(() => {
      // Update message count on mount
      if (typeof App !== 'undefined' && App.unReadMessagesCnt !== undefined) {
        setMessageCount(App.unReadMessagesCnt);
      }

      // Initialize visible items
      const currentRegularItems = items.filter(
        (item) => item.type !== 'splitButton'
      );
      const initialVisible = currentRegularItems
        .filter((item) => item.overflow !== 'always')
        .map((item) => item.id);
      setVisibleItemIds(initialVisible);

      // Handle resize (rAF-throttled for smooth updates)
      let resizeRafId: number | null = null;
      const handleResize = () => {
        if (resizeRafId) return;
        resizeRafId = requestAnimationFrame(() => {
          resizeRafId = null;
          calculateOverflow();
        });
      };
      window.addEventListener('resize', handleResize);

      // Handle document click to close menus
      const handleDocumentClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.officer-toolbar')) {
          setMenuOpen(false);
          setContextMenu((prev) => ({ ...prev, visible: false }));
        }
      };
      document.addEventListener('click', handleDocumentClick);

      // Handle toolbar-update-message-count
      const handleMessageCount = (e: Event) => {
        const detail = (e as CustomEvent).detail;
        setMessageCount(detail.count);
      };
      window.addEventListener(
        'toolbar-update-message-count',
        handleMessageCount
      );

      // Calculate overflow after initial render
      requestAnimationFrame(() => {
        calculateOverflow();
        setupResizeObserver();
      });

      return () => {
        window.removeEventListener('resize', handleResize);
        if (resizeRafId) cancelAnimationFrame(resizeRafId);
        document.removeEventListener('click', handleDocumentClick);
        window.removeEventListener(
          'toolbar-update-message-count',
          handleMessageCount
        );
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Recalculate overflow when items change
    useEffect(() => {
      // Clear stale width cache and show all candidates so they can be measured
      itemWidthCacheRef.current.clear();
      const currentRegularItems = items.filter(
        (item) => item.type !== 'splitButton'
      );
      const allCandidateIds = currentRegularItems
        .filter((item) => item.overflow !== 'always')
        .map((item) => item.id);
      setVisibleItemIds(allCandidateIds);
      // After render with all items visible, measure and calculate overflow
      requestAnimationFrame(() => {
        calculateOverflow();
      });
    }, [items, calculateOverflow]);

    // -- ResizeObserver setup --

    function setupResizeObserver() {
      const el = rootRef.current;
      if (!el) return;

      const container = el.querySelector('.toolbar-container');
      if (!container) return;

      let lastWidth = 0;
      let observerRafId: number | null = null;

      const observer = new ResizeObserver((entries) => {
        if (isCalculatingOverflowRef.current) return;

        const entry = entries[0];
        const currentWidth = entry.contentRect.width;

        if (lastWidth === 0 && currentWidth > 0) {
          lastWidth = currentWidth;
          calculateOverflow();
          return;
        }

        lastWidth = currentWidth;

        if (observerRafId) return;
        observerRafId = requestAnimationFrame(() => {
          observerRafId = null;
          calculateOverflow();
        });
      });

      observer.observe(container);
      resizeObserverRef.current = observer;
    }

    // -- Helper predicates --

    const isActive = (item: ToolbarItem) => effectiveActiveTab === item.id;
    const showBadge = (item: ToolbarItem) =>
      item.id === 'Messaging' && messageCount > 0;
    const isDisabled = (item: ToolbarItem) => disabledItems.has(item.id);
    const isBlinking = (item: ToolbarItem) => blinkingItems.has(item.id);

    // -- Render --

    const combinedMenuTitle = hasOverflowItems
      ? 'More & User Menu'
      : 'User Menu';

    return (
      <div
        ref={rootRef}
        className={`officer-toolbar ${currentTheme} ${sectionType.toLowerCase()}`}
      >
        <div className="toolbar-container">
          <div className="toolbar-main">
            {mainItems.map((item) => (
              <button
                key={item.id}
                id={item.id}
                data-group={sectionType}
                className={
                  'toolbar-btn' +
                  (isActive(item) ? ' active' : '') +
                  (isDisabled(item) ? ' disabled' : '') +
                  (isBlinking(item) ? ' myCallBlink' : '')
                }
                disabled={isDisabled(item)}
                onClick={(e) => handleItemClick(item, e)}
                onContextMenu={(e) => {
                  if (shouldShowContextMenu(item)) {
                    handleContextMenu(item, e);
                  } else {
                    e.preventDefault();
                  }
                }}
              >
                {item.text}
                {showBadge(item) && <span className="badge">{messageCount}</span>}
              </button>
            ))}
          </div>

          <div className="toolbar-menu">
            <button
              className={'menu-btn' + (menuOpen ? ' open' : '')}
              onClick={toggleMenu}
              title={combinedMenuTitle}
            >
              <i className="fas fa-ellipsis-v"></i>
            </button>

            {menuOpen && (
              <div className="combined-menu">
                {hasOverflowItems && (
                  <>
                    <div className="menu-section">
                      {overflowItems.map((item) => (
                        <button
                          key={'overflow-' + item.id}
                          id={item.id}
                          data-group={sectionType}
                          className={
                            'menu-item' +
                            (isActive(item) ? ' active' : '') +
                            (isDisabled(item) ? ' disabled' : '') +
                            (isBlinking(item) ? ' myCallBlink' : '')
                          }
                          disabled={isDisabled(item)}
                          onClick={(e) => handleItemClick(item, e)}
                          onContextMenu={(e) => {
                            if (shouldShowContextMenu(item)) {
                              handleContextMenu(item, e);
                            } else {
                              e.preventDefault();
                            }
                          }}
                        >
                          {item.text}
                          {showBadge(item) && (
                            <span className="badge">{messageCount}</span>
                          )}
                        </button>
                      ))}
                    </div>
                    <div className="menu-divider"></div>
                  </>
                )}

                <div className="menu-section">
                  {userMenuItems.map((menuItem) => (
                    <button
                      key={'user-' + menuItem.id}
                      id={menuItem.id}
                      data-group={sectionType}
                      className={
                        'menu-item' + (isActive(menuItem) ? ' active' : '')
                      }
                      onClick={(e) => handleItemClick(menuItem, e)}
                      onContextMenu={(e) => {
                        if (shouldShowContextMenu(menuItem)) {
                          handleContextMenu(menuItem, e);
                        } else {
                          e.preventDefault();
                        }
                      }}
                    >
                      {menuItem.text}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {contextMenu.visible && contextMenu.item && (
          <div
            className="context-menu"
            style={{
              left: contextMenu.x + 'px',
              top: contextMenu.y + 'px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="context-menu-item"
              onClick={() => openInNewTab(contextMenu.item!)}
            >
              <i className="fas fa-external-link-alt"></i>
              Open in New Tab
            </button>
            <button
              className="context-menu-item"
              onClick={() => openInNewWindow(contextMenu.item!)}
            >
              <i className="fas fa-window-restore"></i>
              Open in New Window
            </button>
            <div className="menu-divider"></div>
            <button
              className="context-menu-item"
              onClick={() => copyLink(contextMenu.item!)}
            >
              <i className="fas fa-link"></i>
              Copy Link
            </button>
          </div>
        )}
      </div>
    );
  }
);

(window as any).OrnaToolbarReact = OrnaToolbar;
